/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useOuterClick from "@cleanup/hooks/useOutsideElement";
import GeneralFooterFilter from "@cleanup/components/GeneralFooterFilter/GeneralFooterFilter";
import { useSelector } from "react-redux";
import searchUIClient from "@/apis/searchUIClient";
import { useOrg } from "@/contexts/OrgContext";
import { Button, Container, Form } from "react-bootstrap";
import { AdvanceFilter, FilterBody, FilterHeaderStyle } from "@cleanup/components/AdvancedFilters/FilterStyle";
import CheckboxItem from "@cleanup/components/AdvancedFilters/CheckboxItem";
import SntCloseSmallIcon from "@icons/SntCloseSmallIcon";
import SntArrowDownIcon from "@icons/SntArrowDownIcon";
import usePositionFilter from "@cleanup/hooks/usePositionFilter";
import SntSelect from "@cleanup/wrappers/ReactSelect/SntSelect";
import { supportedOperatorConstant } from "@/components/SntTableViewCard/SntTableConfigurationConstant";
import SntWarningInfo from "@cleanup/components/SntInfo/SntWarningInfo";
import PartnerIndicator from "@/components/PartnerIndicator/PartnerIndicator";
import { RootState } from "@/types/RootState";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { hasPermission } from "@/hooks/usePermissions";
import { translate } from "@/hooks/useLanguage";
import { UserPermission } from "@/types/generated/UserPermission";


interface StaticOption {
  value: string;
  label: string;
  /** Tag style of the option in the dropdown, e.g. "label label-danger". */
  cssClass?: string;
}

interface FilterTypeSettings {
  optionsURL?: string;
  options?: StaticOption[];
  showOptionSearchFilter?: boolean;
  showOptionHints?: boolean;
  suggestedOptionsTitle?: string;
  supportedOperators?: string[];
  hideOptionHintForSystemAllMenu?: boolean;
}

interface Descriptor {
  _idx: string;
  key?: string;
  label?: string;
  description: string;
  categoryId?: string;
  order?: number;
  filterType?: string;
  filterTypeSettings: FilterTypeSettings;
  searchType?: string;
}

interface FilterValue {
  selectedValues?: (string | number)[];
  operator?: string;
}

interface FilterData {
  filterKey?: string;
  filterType?: string;
  categoryId?: string;
  searchType?: string;
  filterValue?: FilterValue;
  notFilter?: boolean;
  idx?: string;
}

interface MultiCheckboxFilterProps {
  descriptor: Descriptor;
  data?: FilterData;
  onChange?: (data: FilterData | { idx: string; data: null }) => void;
  isShowExclude?: boolean;
  disabled?: boolean;
  deviceCategorySuggestion?: string;
}

interface DataState {
  selectedValues: (string | number)[];
  operator?: string;
}

interface MapDataItem {
  key: string | number;
  label: string;
  optionHint?: number | string;
  cssClass?: string;
  ownedByOrganisationType?: string;
}

interface ListItem {
  key: string | number;
  id: string;
  checked: boolean;
  label: string;
}

interface OperatorOption {
  value: string;
  label: string;
}

const MultiCheckboxFilter: React.FC<MultiCheckboxFilterProps> = ({
  descriptor,
  data = {},
  onChange,
  isShowExclude = true,
  disabled = false,
  deviceCategorySuggestion,
}) => {
  const [description] = useState(descriptor.description || "");
  const [showOptionSearchFilter] = useState(
    (descriptor.filterTypeSettings &&
      descriptor.filterTypeSettings.showOptionSearchFilter) ||
      true
  );
  const loginInfo = useSelector((state: RootState) => state.user);
  const [label, setLabel] = useState(
    descriptor.label || descriptor.description || ""
  );
  const [title, setTitle] = useState("");

  // The asset-type filter renders its options as the blue, smart-collapsing path chip (same as the
  // asset-type chips elsewhere), instead of the shared "asset-tag-render" beige tag.
  const isAssetTypeFilter = descriptor.key === "assetTypes";

  const [searchText, setSearchText] = useState("");
  const [isShow, setShow] = useState(false);

  const [dataState, setDataState] = useState<DataState>({
    selectedValues: [],
  });

  const [mapData, setMapData] = useState<Record<string, MapDataItem>>({});
  const [keysMap, setKeysMap] = useState<Record<string | number, string>>({});
  const { orgId } = useOrg();
  const [selectedList, setSelectedList] = useState<ListItem[]>([]);

  const [unselectedList, setUnselectedList] = useState<ListItem[]>([]);

  const [invalidValues, setInvalidValues] = useState<(string | number)[]>([]);

  const tempSelectedList = useRef<(string | number)[]>([]);

  const categoryItemRef = useRef<HTMLDivElement>(null);
  const [availableUnselectedList, setAvailableUnselectedList] = useState<ListItem[]>([]);
  const [categoryIndex, setCategoryIndex] = useState(-1);
  const [delayLoading, setDelayLoading] = useState<NodeJS.Timeout | null>(null);
  const isScroll = useRef(true);
  const [isLoading, setIsLoading] = useState(true);

  const popupStyleRef = useRef<Record<string, unknown>>({});
  const { popupRef, buttonRef, getPosition } = usePositionFilter();
  const [operatorOptions, setOperatorOptions] = useState<OperatorOption[]>([]);
  const isMountedRef = useRef(true);

  const updateTempSelectedList = (selectedList: ListItem[]) => {
    selectedList.forEach((item, index) => {
      if (tempSelectedList.current.indexOf(Number.parseInt(String(item.key))) !== -1) {
        selectedList[index].checked = true;
      }
    });
  };

  const getUnselectedList = (data?: DataState): ListItem[] => {
    if (data && data instanceof Object) {
      return Object.keys(mapData)
        .filter(
          (id) =>
            data.selectedValues.find((item) => item == mapData[id].key) ===
            undefined
        )
        .map((id) => {
          return {
            key: mapData[id].key,
            id: id,
            checked: false,
            label: mapData[id].label,
          };
        });
    }
    return Object.keys(mapData)
      .filter(
        (id) =>
          dataState.selectedValues.find((item) => item == mapData[id].key) ===
          undefined
      )
      .map((id) => {
        return {
          key: mapData[id].key,
          id: id,
          checked: false,
          label: mapData[id].label,
        };
      });
  };

  const getSelectedList = (data?: DataState): ListItem[] => {
    if (Object.keys(keysMap).length === 0) return [];

    if (data && data instanceof Object) {
      const allSelectedValues = data.selectedValues || [];
      const validValues = allSelectedValues.filter(
        (key) => keysMap[key] !== undefined && mapData[keysMap[key]]
      );
      const invalidValuesFound = allSelectedValues.filter(
        (key) => keysMap[key] === undefined || !mapData[keysMap[key]]
      );

      setInvalidValues(invalidValuesFound);

      return validValues.map((key) => {
        return {
          key: key,
          id: keysMap[key],
          checked: true,
          label: mapData[keysMap[key]].label,
        };
      });
    }
    const allSelectedValues = dataState.selectedValues || [];
    const validValues = allSelectedValues.filter(
      (key) => keysMap[key] !== undefined && mapData[keysMap[key]]
    );
    const invalidValuesFound = allSelectedValues.filter(
      (key) => keysMap[key] !== undefined || !mapData[keysMap[key]]
    );

    setInvalidValues(invalidValuesFound);

    return validValues.map((key) => {
      return {
        key: key,
        id: keysMap[key],
        checked: true,
        label: mapData[keysMap[key]].label,
      };
    });
  };

  useEffect(() => {
    setCategoryIndex(0);
    setAvailableUnselectedList([...unselectedList.slice(0, 49)]);
    if (isScroll.current && categoryItemRef.current) {
      categoryItemRef.current.scrollTo(0, selectedList.length * 41);
    }
  }, [selectedList.length, unselectedList]);

  useEffect(() => {
    if (categoryIndex !== 0) {
      setAvailableUnselectedList([
        ...availableUnselectedList,
        ...unselectedList.slice(
          categoryIndex * 50,
          categoryIndex * 50 + 50 - 1
        ),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIndex]);

  const onScrollDialog = () => {
    if (categoryItemRef.current) {
      if (
        categoryItemRef.current.scrollHeight - categoryItemRef.current.scrollTop <
        400
      ) {
        if (delayLoading) {
          clearTimeout(delayLoading);
        }
        setDelayLoading(
          setTimeout(function () {
            setCategoryIndex((pre) => pre + 1);
          }, 100)
        );
      }
    }
  };

  useEffect(() => {
    isScroll.current = true;
    if (Object.keys(keysMap).length === 0 || Object.keys(mapData).length === 0) {
      return;
    }
    let _unselectedList = getUnselectedList();
    if (searchText.trim() !== "") {
      _unselectedList = _unselectedList.filter(
        (s) => s.label.toLowerCase().indexOf(searchText.toLowerCase()) !== -1
      );
    }
    updateTempSelectedList(_unselectedList);
    setUnselectedList(_unselectedList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysMap, mapData, searchText]);

  useEffect(() => {
    setLabel(descriptor.label || descriptor.description || "");

    const { filterValue } = data;
    let _title = translate(LanguageKey.all_key);
    const _dataState: DataState = {
      selectedValues: [],
    };

    if (filterValue) {
      Object.assign(_dataState, { selectedValues: [], ...filterValue });
      if (
        filterValue.selectedValues &&
        Object.keys(keysMap).length !== 0 &&
        Object.keys(mapData).length !== 0
      ) {
        if (filterValue.selectedValues.length > 2) {
          _title = String(filterValue.selectedValues.length);
        } else if (filterValue.selectedValues.length > 0) {
          _title = filterValue.selectedValues
            .map((s) => keysMap[s])
            .map((s) => (mapData[s] && mapData[s].label) || "")
            .filter((s) => s)
            .join(", ");
        }
      }

      if (
        filterValue.operator &&
        filterValue.operator !== supportedOperatorConstant.IN
      ) {
        const operatorKey = `filter_operator_${filterValue.operator}_key` as LanguageKey;
        _title = translate(operatorKey) || "";
      }
    }
    setDataState(_dataState);
    setTitle(_title);

    if (_dataState && _dataState.selectedValues) {
      const _unselectedList = getUnselectedList(_dataState);
      const _selectedList = getSelectedList(_dataState);

      setUnselectedList(_unselectedList);
      setSelectedList(_selectedList);
    }

    if (
      descriptor.filterTypeSettings &&
      descriptor.filterTypeSettings.supportedOperators &&
      descriptor.filterTypeSettings.supportedOperators.length > 1
    ) {
      const supportedOperators = descriptor.filterTypeSettings.supportedOperators;
      const supportedOperatorOptions: OperatorOption[] = [];
      supportedOperators.forEach((operator) => {
        const operatorKey = `filter_operator_${operator}_key` as LanguageKey;
        supportedOperatorOptions.push({
          value: operator,
          label: translate(operatorKey) || operator,
        });
      });

      setOperatorOptions(supportedOperatorOptions);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, data.filterValue, descriptor.filterTypeSettings, mapData, keysMap]);

  useEffect(
    () => {
      isMountedRef.current = true;

      // Handle static options defined in filterTypeSettings.options
      if (descriptor.filterTypeSettings.options) {
        const _options = descriptor.filterTypeSettings.options;
        const _mapData: Record<string, MapDataItem> = {};
        const _keysMap: Record<string | number, string> = {};

        for (let j = 0, _size = _options.length; j < _size; j++) {
          const dataItem = _options[j];
          const _key = dataItem.value;
          const _idx = "suggestion_" + j;

          _mapData[_idx] = {
            key: _key,
            label: dataItem.label,
            cssClass: dataItem.cssClass,
          };
          _keysMap[_key] = _idx;
        }

        setMapData(_mapData);
        setKeysMap(_keysMap);
        setIsLoading(false);
      }
      // Handle dynamic options from URL
      else if (descriptor.filterTypeSettings.optionsURL) {
        const param =
          descriptor.searchType === "DYNAMIC" ? "/" + descriptor.key : "";
        const orgParam = orgId && !disabled ? `?org=${orgId}` : "";

        let categorySuggestionParam = "";
        if (deviceCategorySuggestion && orgParam && orgParam !== "") {
          categorySuggestionParam = `&deviceCategorySuggestion=${deviceCategorySuggestion}`;
        } else if (deviceCategorySuggestion) {
          categorySuggestionParam = `?deviceCategorySuggestion=${deviceCategorySuggestion}`;
        }

        searchUIClient
          .getSuggestion({
            url:
              descriptor.filterTypeSettings.optionsURL +
              param +
              orgParam +
              categorySuggestionParam,
          })
          .then(({ data }) => {
            if (!isMountedRef.current) return;

            const _options = data.options;
            const _mapData: Record<string, MapDataItem> = {};
            const _keysMap: Record<string | number, string> = {};

            for (let j = 0, _size = _options.length; j < _size; j++) {
              const dataItem = _options[j];
              const _key = dataItem.key;
              const _idx = "suggestion_" + j;

              _mapData[_idx] = dataItem;
              _keysMap[_key] = _idx;
            }

            setMapData(_mapData);
            setKeysMap(_keysMap);
            setIsLoading(false);
          })
          .catch((error) => {
            if (!isMountedRef.current) return;
            console.log(error);
            setIsLoading(false);
          });
      } else {
        setIsLoading(false);
      }

      return () => {
        isMountedRef.current = false;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [descriptor.filterTypeSettings.optionsURL, descriptor.filterTypeSettings.options, orgId]
  );

  const getData = useCallback(() => {
    const newData = JSON.parse(JSON.stringify(data)) as FilterData;
    newData.filterValue = {
      selectedValues: Array.from(
        new Set([
          ...selectedList.filter((s) => s.checked).map((s) => s.key.toString()),
          ...unselectedList
            .filter((s) => s.checked)
            .map((s) => s.key.toString()),
          ...tempSelectedList.current.map((key) => key.toString()),
        ])
      ),
      operator: dataState.operator,
    };
    return newData;
  }, [data, dataState, selectedList, unselectedList]);

  const isChanged = useCallback(() => {
    const oldDataFilter = data.filterValue || {};
    const newData = getData();
    const newDataFilter = newData.filterValue || {};

    if (
      JSON.stringify([...(oldDataFilter.selectedValues || [])].sort()) ===
        JSON.stringify([...(newDataFilter.selectedValues || [])].sort()) &&
      oldDataFilter.operator === newDataFilter.operator
    ) {
      return false;
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dataState, selectedList, unselectedList]);

  const onAppliedFilter = () => {
    setShow(false);
    if (isChanged()) {
      if (onChange) {
        onChange(getData());
      }
    }
    tempSelectedList.current = [];
  };

  const onClearFilter = () => {
    setShow(false);
    if (onChange) {
      onChange({
        idx: descriptor._idx,
        data: null,
      });
    }
    tempSelectedList.current = [];
  };

  const onClickButton = () => {
    if (isShow) {
      setShow(false);
      if (isChanged()) {
        if (onChange) {
          onChange(getData());
        }
      }
    } else {
      tempSelectedList.current = [];
      popupStyleRef.current = getPosition();
      setShow(true);
    }
  };

  const onFilterItems = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const refWrapper = useOuterClick((e) => {
    if (isShow && (e.target as HTMLElement).id.indexOf("react-select") === -1) {
      setShow(false);
      if (isChanged()) {
        if (onChange) {
          onChange(getData());
        }
      }
    }
  });

  const availableOptionView = useMemo(() => {
    const { filterTypeSettings } = descriptor;
    if (filterTypeSettings) {
      return (
        <>
          {operatorOptions.length > 1 && (
            <Container className="pt-3">
              <SntSelect
                className="flex-grow-1"
                value={operatorOptions.filter(
                  (option) =>
                    option.value ===
                    (dataState.operator || operatorOptions[0].value)
                )}
                options={operatorOptions}
                onChange={(operatorOption: OperatorOption | null) => {
                  setDataState({
                    ...dataState,
                    operator: operatorOption?.value,
                  });
                }}
              />
            </Container>
          )}
        </>
      );
    }
    return null;
  }, [dataState, descriptor, operatorOptions]);

  const selectedOptionView = useMemo(() => {
    const _defaultClass = "label label-stop";

    const _selectedList = Array.from(selectedList);
    const selectedOptions = _selectedList.map(({ id, checked }, index) => {
      return (
        <CheckboxItem
          key={index}
          checked={checked}
          onChange={(value) => {
            _selectedList[index].checked = value;
            setSelectedList(_selectedList);
          }}
          optionHint={
            hasPermission(UserPermission.SYSTEM_ADMIN_VIEW) &&
            orgId == null &&
            descriptor.filterTypeSettings.hideOptionHintForSystemAllMenu
              ? ""
              : descriptor.filterTypeSettings.showOptionHints
              ? mapData[id].optionHint
              : ""
          }
          cssClass={mapData[id].cssClass || _defaultClass}
          label={
            mapData[id].ownedByOrganisationType ? (
              <span>
                {mapData[id].label}
                {mapData[id].ownedByOrganisationType && (
                  <PartnerIndicator
                    organisationType={mapData[id].ownedByOrganisationType as "SYSTEM" | "PARTNER" | "NORMAL"}
                    currentUserOrgType={
                      loginInfo?.organisation?.organisationType as "SYSTEM" | "PARTNER" | "NORMAL" | undefined
                    }
                    size="sm"
                  />
                )}
              </span>
            ) : (
              mapData[id].label
            )
          }
        />
      );
    });
    return <Container>{selectedOptions}</Container>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedList, descriptor]);

  const unselectedOptionView = useMemo(() => {
    const _defaultClass = "label label-stop";

    const _unselectedList = Array.from(availableUnselectedList);
    const selectedOptions = availableUnselectedList.map(
      ({ id, checked }, index) => {
        return (
          <CheckboxItem
            key={id}
            checked={checked}
            onChange={(value) => {
              _unselectedList[index].checked = value;
              isScroll.current = false;
              setAvailableUnselectedList(_unselectedList);
              if (value) {
                tempSelectedList.current = [
                  ...tempSelectedList.current,
                  mapData[id].key.toString(),
                ];
              } else {
                tempSelectedList.current.splice(
                  tempSelectedList.current.indexOf(mapData[id].key.toString()),
                  1
                );
                tempSelectedList.current = [...tempSelectedList.current];
              }
            }}
            cssClass={
              isAssetTypeFilter
                ? "asset-type-path-render"
                : mapData[id].cssClass || _defaultClass
            }
            label={
              mapData[id].ownedByOrganisationType ? (
                <span>
                  {mapData[id].label}
                  {mapData[id].ownedByOrganisationType && (
                    <PartnerIndicator
                      organisationType={mapData[id].ownedByOrganisationType as "SYSTEM" | "PARTNER" | "NORMAL"}
                      currentUserOrgType={
                        loginInfo?.organisation?.organisationType as "SYSTEM" | "PARTNER" | "NORMAL" | undefined
                      }
                      size="sm"
                    />
                  )}
                </span>
              ) : (
                mapData[id].label
              )
            }
            optionHint={
              hasPermission(UserPermission.SYSTEM_ADMIN_VIEW) &&
              orgId == null &&
              descriptor.filterTypeSettings.hideOptionHintForSystemAllMenu
                ? ""
                : descriptor.filterTypeSettings.showOptionHints
                ? mapData[id].optionHint
                : ""
            }
          />
        );
      }
    );
    return (
      <Container>
        {descriptor.filterTypeSettings.suggestedOptionsTitle && (
          <div className="fw-bold mt-3 mb-3">
            {descriptor.filterTypeSettings.suggestedOptionsTitle}
          </div>
        )}
        {selectedOptions}
      </Container>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableUnselectedList, descriptor]);

  return (
    <AdvanceFilter ref={refWrapper}>
      <Button
        ref={buttonRef}
        variant="sensolus-greylight"
        title={description}
        disabled={disabled || isLoading}
        onClick={onClickButton}
      >
        {invalidValues.length > 0 ? (
          <>
            <div className="d-flex align-items-center">
              {(dataState.selectedValues.length > 0 || dataState.operator) && (
                <SntCloseSmallIcon
                  className="me-1"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClearFilter();
                  }}
                />
              )}
              <div>{label}</div>
              <div className="d-flex align-items-center ms-1">
                <SntWarningInfo className="mb-0 p-0">
                  {translate(LanguageKey.filter_invalid_key)}
                </SntWarningInfo>
              </div>
              <SntArrowDownIcon />
            </div>
          </>
        ) : (
          <>
            {(dataState.selectedValues.length > 0 || dataState.operator) && (
              <SntCloseSmallIcon
                className="me-1"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClearFilter();
                }}
              />
            )}
            <span>{label}</span>:{" "}
            <FilterHeaderStyle>{title}</FilterHeaderStyle>
            <SntArrowDownIcon />
          </>
        )}
      </Button>
      <FilterBody
        ref={popupRef}
        style={{ display: isShow ? "block" : "none", ...popupStyleRef.current }}
      >
        {availableOptionView}
        <Container className="pt-3">
          {showOptionSearchFilter && (
            <div className="mb-3">
              <Form.Control
                name="search"
                type="text"
                value={searchText}
                placeholder={translate(LanguageKey.input_search_key)}
                onChange={onFilterItems}
              />
            </div>
          )}
          {invalidValues.length > 0 && (
            <SntWarningInfo>
              <div
                dangerouslySetInnerHTML={{
                  __html: translate(LanguageKey.invalid_value_detected_key),
                }}
              />
            </SntWarningInfo>
          )}
        </Container>
        <div
          ref={categoryItemRef}
          onScroll={onScrollDialog}
          id="scroll-category-item"
          style={{ overflowY: "auto", maxHeight: "300px" }}
        >
          {![
            supportedOperatorConstant.NOT_NULL,
            supportedOperatorConstant.NULL,
          ].includes(dataState.operator as any || "") &&
            selectedList.length > 0 && (
              <>
                {selectedOptionView}
                <div className="mb-3 mt-3 border-top border-sensolus-grey"></div>
              </>
            )}

          {![
            supportedOperatorConstant.NOT_NULL,
            supportedOperatorConstant.NULL,
          ].includes(dataState.operator as any || "") && (
            <div style={{ position: "relative" }}>{unselectedOptionView}</div>
          )}
        </div>
        <GeneralFooterFilter
          onClearFilter={() => onClearFilter()}
          onAppliedFilter={() => onAppliedFilter()}
          isShowExclude={false}
        />
      </FilterBody>
    </AdvanceFilter>
  );
};

export default MultiCheckboxFilter;
